import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  HatGlasses,
  Loader2,
  Search,
} from 'lucide-react';

import AccessDeniedMessage from '../components/AccessDeniedMessage';
import AppFooter from '../components/AppFooter';
import AppHeader from '../components/AppHeader';
import EnvBanner from '../components/EnvBanner';
import NotFoundMessage from '../components/NotFoundMessage';
import BlockedNotice from '../components/redaction/BlockedNotice';
import CandidateList from '../components/redaction/CandidateList';
import CategoryStep from '../components/redaction/CategoryStep';
import DoneStep from '../components/redaction/DoneStep';
import RedactionDocumentView, {
  type RedactionDocumentViewHandle,
} from '../components/redaction/RedactionDocumentView';
import ReviewFooterStatus from '../components/redaction/ReviewFooterStatus';
import { useApiRoot } from '../contexts/ApiRootContext';
import { useRecordLinks } from '../hooks/useRecordLinks';
import {
  RedactionRequestError,
  createRedaction,
  fetchRedactionCandidates,
  fetchRedactionCategories,
  resolveRedactionDownloadUrl,
} from '../lib/redactionApi';
import type {
  CandidateReport,
  ManualRedaction,
  RedactionBox,
  RedactionCategory,
  RedactionCategoryInfo,
  RedactionManifest,
  RedactionOverlayBox,
  RedactionSelectionPayload,
} from '../types/redaction';

const BRAND_BG = 'bg-emerald-800';

/** How long an item stays highlighted after something points the reviewer at it. */
const HIGHLIGHT_MS = 2000;

type Step = 'categories' | 'review' | 'done';

const STEPS: { value: Step; label: string }[] = [
  { value: 'categories', label: 'Choose what to find' },
  { value: 'review', label: 'Review' },
  { value: 'done', label: 'Download' },
];

/**
 * Redact a record, from choosing what to look for through to downloading the
 * copy.
 *
 * Takes the whole window so the pages can be read at a useful size. The
 * record's links are loaded here rather than passed in, so the page can be
 * opened directly and still follow what the API says is allowed.
 */
export default function RedactionPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id, versionId } = useParams<{ id: string; versionId?: string }>();
  const { getHref } = useApiRoot();
  const { links, title, loading, error, accessDenied } = useRecordLinks(id, versionId);

  const recordPath = versionId
    ? `/records/${id}/versions/${versionId}`
    : `/records/${id}`;

  const backToRecord = () => {
    if (location.key !== 'default') navigate(-1);
    else navigate(recordPath, { replace: true });
  };
  const categoriesHref = getHref('redaction_categories');
  const candidatesHref = links.redaction_candidates?.href ?? null;
  const createHref = links.create_redaction?.href ?? null;

  let content: React.ReactNode;
  if (loading) {
    content = (
      <div className="flex flex-1 flex-col items-center justify-center gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
        <p className="text-sm text-gray-500">Loading record...</p>
      </div>
    );
  } else if (accessDenied) {
    content = (
      <div className="flex-1 overflow-y-auto px-4">
        <AccessDeniedMessage
          message="You don't have permission to view this record."
          onGoBack={() => navigate(-1)}
        />
      </div>
    );
  } else if (error || !id) {
    content = (
      <div className="flex-1 overflow-y-auto px-4">
        <NotFoundMessage
          message={error || 'The requested record could not be loaded.'}
          onGoBack={() => navigate(-1)}
        />
      </div>
    );
  } else if (!categoriesHref || !candidatesHref || !createHref) {
    content = (
      <div className="flex-1 overflow-y-auto px-4">
        <NotFoundMessage
          title="Not available"
          message="Redaction is not available for this record."
          onGoBack={backToRecord}
        />
      </div>
    );
  } else {
    content = (
      <RedactionWorkflow
        recordId={id}
        recordTitle={title}
        onBackToRecord={backToRecord}
        categoriesHref={categoriesHref}
        candidatesHref={candidatesHref}
        createHref={createHref}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-white">
      <EnvBanner />
      <AppHeader />
      {content}
      <AppFooter />
    </div>
  );
}

interface RedactionWorkflowProps {
  recordId: string;
  recordTitle: string | null;
  onBackToRecord: () => void;
  categoriesHref: string;
  candidatesHref: string;
  createHref: string;
}

function RedactionWorkflow({
  recordId,
  recordTitle,
  onBackToRecord,
  categoriesHref,
  candidatesHref,
  createHref,
}: RedactionWorkflowProps) {
  const viewerRef = useRef<RedactionDocumentViewHandle>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const highlightTimer = useRef<number | null>(null);

  const [step, setStep] = useState<Step>('categories');
  // What the API can search for, and which of those the reviewer has chosen.
  const [availableCategories, setAvailableCategories] = useState<
    RedactionCategoryInfo[]
  >([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categories, setCategories] = useState<RedactionCategory[]>([]);
  const [termsInput, setTermsInput] = useState('');
  const [report, setReport] = useState<CandidateReport | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [grounds, setGrounds] = useState<Record<string, string>>({});
  // Only categories the reviewer changed; anything absent keeps what was found.
  const [categoryOverrides, setCategoryOverrides] = useState<
    Record<string, RedactionCategory>
  >({});
  const [activeId, setActiveId] = useState<string | null>(null);
  const [manualItems, setManualItems] = useState<ManualRedaction[]>([]);
  const [note, setNote] = useState('');
  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blockedReason, setBlockedReason] = useState<string | null>(null);
  const [manifest, setManifest] = useState<RedactionManifest | null>(null);

  // The list comes from the API so the interface offers exactly what the
  // backend searches for. Everything is selected by default, which fails
  // towards withholding rather than releasing.
  useEffect(() => {
    let cancelled = false;
    fetchRedactionCategories(categoriesHref)
      .then((loaded) => {
        if (cancelled) return;
        setAvailableCategories(loaded);
        setCategories(loaded.map((category) => category.value));
      })
      .catch(() => {
        if (cancelled) return;
        setAvailableCategories([]);
        setError('The categories to search for could not be loaded. Go back and try again.');
      })
      .finally(() => {
        if (!cancelled) setCategoriesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [categoriesHref]);

  const groundsByValue = useMemo(() => {
    const entries = report?.withholding_grounds ?? [];
    return new Map(entries.map((ground) => [ground.value, ground]));
  }, [report]);

  const selectedCandidates = useMemo(
    () => (report?.candidates ?? []).filter((c) => selectedIds.has(c.id)),
    [report, selectedIds],
  );

  const visibleBoxes = useMemo<RedactionOverlayBox[]>(
    () =>
      selectedCandidates.flatMap((candidate) =>
        candidate.boxes.map((box) => ({ ownerId: candidate.id, box })),
      ),
    [selectedCandidates],
  );

  const drawnBoxes = useMemo<RedactionOverlayBox[]>(
    () => manualItems.map((item) => ({ ownerId: item.id, box: item.box })),
    [manualItems],
  );

  // The category in force for each candidate: what was found, unless changed.
  const categoryByCandidate = useMemo(
    () =>
      Object.fromEntries(
        (report?.candidates ?? []).map((candidate) => [
          candidate.id,
          categoryOverrides[candidate.id] ?? candidate.category,
        ]),
      ),
    [report, categoryOverrides],
  );

  useEffect(
    () => () => {
      if (highlightTimer.current !== null) window.clearTimeout(highlightTimer.current);
    },
    [],
  );

  // Bring an item in the list into view and mark it for a moment, so the
  // reviewer can tell which one a rectangle or the status line meant.
  const revealItem = useCallback((itemId: string) => {
    const list = listRef.current;
    const row = Array.from(
      list?.querySelectorAll<HTMLElement>('[data-item-id]') ?? [],
    ).find((element) => element.dataset.itemId === itemId);
    if (!list || !row) return;
    // Scroll the list itself rather than the row, so no other container moves.
    const offset =
      row.getBoundingClientRect().top -
      list.getBoundingClientRect().top +
      list.scrollTop;
    list.scrollTo({
      top: Math.max(0, offset - (list.clientHeight - row.offsetHeight) / 2),
      behavior: 'smooth',
    });
    setActiveId(itemId);
    if (highlightTimer.current !== null) window.clearTimeout(highlightTimer.current);
    highlightTimer.current = window.setTimeout(() => {
      setActiveId(null);
      highlightTimer.current = null;
    }, HIGHLIGHT_MS);
  }, []);

  const needsPublicInterestNote = [
    ...selectedCandidates.map((candidate) => grounds[candidate.id] ?? ''),
    ...manualItems.map((item) => item.ground),
  ].some((value) => {
    const ground = groundsByValue.get(value);
    return ground ? !ground.conclusive : false;
  });

  const missingGrounds = selectedCandidates.filter(
    (candidate) => !grounds[candidate.id],
  );
  const incompleteManual = manualItems.filter(
    (item) => !item.category || !item.ground,
  );

  // Drawn boxes are listed above candidates, so this is the first one on screen.
  const showFirstMissing = () => {
    const first = incompleteManual[0]?.id ?? missingGrounds[0]?.id;
    if (first) revealItem(first);
  };

  const canCreate =
    (selectedCandidates.length > 0 || manualItems.length > 0) &&
    missingGrounds.length === 0 &&
    incompleteManual.length === 0 &&
    (!needsPublicInterestNote || note.trim().length > 0);

  const handleFind = async () => {
    setIsWorking(true);
    setError(null);
    setBlockedReason(null);
    try {
      const terms = termsInput
        .split('\n')
        .map((term) => term.trim())
        .filter(Boolean);
      const found = await fetchRedactionCandidates(candidatesHref, {
        categories,
        terms,
      });

      // Everything found is selected by default, with its suggested ground.
      // The reviewer removes what should stay rather than hunting for what
      // should go, which fails towards withholding rather than releasing.
      setReport(found);
      setSelectedIds(new Set(found.candidates.map((candidate) => candidate.id)));
      setGrounds(
        Object.fromEntries(
          found.candidates
            .filter((candidate) => candidate.suggested_ground)
            .map((candidate) => [candidate.id, candidate.suggested_ground as string]),
        ),
      );
      setCategoryOverrides({});
      setManualItems([]);
      setStep('review');
    } catch (caught) {
      if (caught instanceof RedactionRequestError && caught.reason) {
        setBlockedReason(caught.reason);
      } else {
        setError(
          caught instanceof Error ? caught.message : 'Could not analyse this record',
        );
      }
    } finally {
      setIsWorking(false);
    }
  };

  const handleCreate = async () => {
    setIsWorking(true);
    setError(null);
    try {
      const selections: RedactionSelectionPayload[] = [
        ...selectedCandidates.map((candidate) => ({
          term: candidate.text,
          also_terms: candidate.also_covers ?? [],
          category: categoryByCandidate[candidate.id] ?? candidate.category,
          ground: grounds[candidate.id],
          boxes: candidate.boxes,
          origin: candidate.located_by === 'ocr' ? ('ocr' as const) : ('term' as const),
        })),
        ...manualItems.map((item) => ({
          term: null,
          category: item.category as RedactionCategory,
          ground: item.ground,
          boxes: [item.box],
          origin: 'manual' as const,
        })),
      ];
      const created = await createRedaction(createHref, {
        selections,
        public_interest_note: note.trim() || null,
        source_generation: report?.source_generation ?? null,
      });
      setManifest(created);
      setStep('done');
    } catch (caught) {
      if (caught instanceof RedactionRequestError && caught.reason) {
        // The record moved under the review, so the candidates in hand are
        // stale. Send the reviewer back rather than leave them retrying a
        // selection that can no longer be applied.
        setBlockedReason(caught.reason);
        setStep('categories');
        setReport(null);
        setManualItems([]);
      } else {
        setError(
          caught instanceof Error ? caught.message : 'Could not create the copy',
        );
      }
    } finally {
      setIsWorking(false);
    }
  };

  const handleDownload = async () => {
    const href = manifest?._links?.download?.href;
    if (!href) return;
    try {
      const url = await resolveRedactionDownloadUrl(href);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setError('Could not generate the download link');
    }
  };

  const toggleCandidate = (id: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Changing what something is can change why it is withheld. Follow the new
  // category's usual ground, but only where the reviewer had not picked their
  // own, so a deliberate choice is never overwritten.
  const changeCandidateCategory = (id: string, value: RedactionCategory) => {
    const candidate = report?.candidates.find((entry) => entry.id === id);
    if (!candidate) return;
    const previous = categoryByCandidate[id] ?? candidate.category;
    if (value === previous) return;

    setCategoryOverrides((current) => ({ ...current, [id]: value }));

    const defaults = report?.default_grounds ?? {};
    const currentGround = grounds[id] ?? '';
    const wasUntouched =
      !currentGround ||
      currentGround === defaults[previous] ||
      currentGround === candidate.suggested_ground;
    const nextGround = defaults[value];
    if (wasUntouched && nextGround) {
      setGrounds((current) => ({ ...current, [id]: nextGround }));
    }
  };

  const handleDrawBox = (box: RedactionBox) => {
    const id = `drawn-${box.page}-${Math.round(box.x0)}-${Math.round(box.y0)}-${Date.now()}`;
    setManualItems((current) => [
      ...current,
      { id, category: '', ground: '', box },
    ]);
  };

  const updateManualItem = (id: string, patch: Partial<ManualRedaction>) => {
    setManualItems((current) =>
      current.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    );
  };

  const stepIndex = STEPS.findIndex((entry) => entry.value === step);

  return (
    <>
      <header className="flex shrink-0 items-center gap-4 border-b border-gray-100 px-4 py-3">
        <button
          type="button"
          onClick={onBackToRecord}
          disabled={isWorking}
          className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-gray-600 transition hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="hidden sm:inline">Back to record</span>
        </button>

        <div className="flex min-w-0 items-center gap-3">
          <div className="rounded-full bg-emerald-100 p-2">
            <HatGlasses className="h-5 w-5 text-emerald-700" />
          </div>
          <div className="min-w-0">
            <h1 className="text-base font-bold text-gray-900">Redact &amp; Download</h1>
            {recordTitle && (
              <p className="truncate text-xs text-gray-500" title={recordTitle}>
                {recordTitle}
              </p>
            )}
          </div>
        </div>

        <ol className="ml-auto hidden items-center gap-2 text-xs font-semibold md:flex">
          {STEPS.map((entry, index) => {
            const active = index === stepIndex;
            const complete = index < stepIndex;
            return (
              <li key={entry.value} className="flex items-center gap-2">
                {index > 0 && <span className="h-px w-6 bg-gray-200" />}
                <span
                  className={`flex items-center gap-1.5 ${
                    active ? 'text-emerald-800' : complete ? 'text-gray-600' : 'text-gray-400'
                  }`}
                  aria-current={active ? 'step' : undefined}
                >
                  <span
                    className={`flex h-5 w-5 items-center justify-center rounded-full text-[11px] tabular-nums ${
                      active
                        ? `${BRAND_BG} text-white`
                        : complete
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {index + 1}
                  </span>
                  {entry.label}
                </span>
              </li>
            );
          })}
        </ol>
      </header>

      {blockedReason && (
        <BlockedNotice
          reason={blockedReason}
          onDismiss={() => setBlockedReason(null)}
        />
      )}

      {error && (
        <div className="mx-6 mt-4 flex shrink-0 items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {step === 'categories' && !blockedReason && (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <CategoryStep
            available={availableCategories}
            loading={categoriesLoading}
            categories={categories}
            onToggle={(value) =>
              setCategories((current) =>
                current.includes(value)
                  ? current.filter((entry) => entry !== value)
                  : [...current, value],
              )
            }
            termsInput={termsInput}
            onTermsChange={setTermsInput}
          />
        </div>
      )}

      {/* While blocked, the categories step is hidden but still the step. */}
      {step === 'categories' && blockedReason && <div className="flex-1" />}

      {step === 'review' && report && (
        <div className="grid min-h-0 flex-1 grid-cols-1 grid-rows-[minmax(0,2fr)_minmax(0,3fr)] overflow-hidden lg:grid-cols-[minmax(320px,400px)_minmax(0,1fr)] lg:grid-rows-1">
          <div
            ref={listRef}
            className="min-h-0 overflow-y-auto border-b border-gray-100 lg:border-b-0 lg:border-r"
          >
            <CandidateList
              report={report}
              availableCategories={availableCategories}
              selectedIds={selectedIds}
              grounds={grounds}
              categories={categoryByCandidate}
              activeId={activeId}
              manualItems={manualItems}
              onToggle={toggleCandidate}
              onGroundChange={(id, value) =>
                setGrounds((current) => ({ ...current, [id]: value }))
              }
              onCategoryChange={changeCandidateCategory}
              onFocusCandidate={(candidate) => {
                if (candidate.boxes.length > 0) {
                  viewerRef.current?.scrollToBox(candidate.boxes[0]);
                }
              }}
              onManualChange={updateManualItem}
              onManualRemove={(id) =>
                setManualItems((current) => current.filter((item) => item.id !== id))
              }
              onFocusManual={(item) => viewerRef.current?.scrollToBox(item.box)}
            />
          </div>
          <div className="min-h-0 min-w-0">
            <RedactionDocumentView
              ref={viewerRef}
              recordId={recordId}
              pages={report.pages}
              pagesWithImages={report.pages_with_images}
              pagesOcrd={report.pages_ocrd}
              boxes={visibleBoxes}
              drawnBoxes={drawnBoxes}
              onDrawBox={handleDrawBox}
              onSelectBox={revealItem}
            />
          </div>
        </div>
      )}

      {step === 'done' && manifest && (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <DoneStep
            manifest={manifest}
            recordTitle={recordTitle}
            onDownload={handleDownload}
            onBack={onBackToRecord}
          />
        </div>
      )}

      {step !== 'done' && (
        <footer className="flex shrink-0 items-center justify-between gap-4 border-t border-gray-100 bg-gray-50 px-6 py-3">
          {step === 'review' && report ? (
            <ReviewFooterStatus
              selectedCount={selectedCandidates.length + manualItems.length}
              missingGroundCount={missingGrounds.length + incompleteManual.length}
              onShowMissing={showFirstMissing}
              needsNote={needsPublicInterestNote}
              note={note}
              onNoteChange={setNote}
            />
          ) : (
            <div className="text-sm text-gray-500">
              Nothing is changed until you create the copy.
            </div>
          )}

          <div className="flex shrink-0 items-center gap-3">
            {step === 'review' && (
              <button
                type="button"
                onClick={() => setStep('categories')}
                disabled={isWorking}
                className="rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-100 disabled:opacity-50"
              >
                Back
              </button>
            )}
            {step === 'categories' && (
              <button
                type="button"
                onClick={handleFind}
                disabled={isWorking || categories.length === 0}
                className={`flex items-center rounded-xl px-6 py-2.5 text-sm font-bold text-white shadow-lg shadow-emerald-200 transition disabled:cursor-not-allowed disabled:opacity-50 ${BRAND_BG}`}
              >
                {isWorking ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Search className="mr-2 h-4 w-4" />
                )}
                {isWorking ? 'Analysing...' : 'Find content'}
              </button>
            )}
            {step === 'review' && (
              <button
                type="button"
                onClick={handleCreate}
                disabled={isWorking || !canCreate}
                className={`flex items-center rounded-xl px-6 py-2.5 text-sm font-bold text-white shadow-lg shadow-emerald-200 transition disabled:cursor-not-allowed disabled:opacity-50 ${BRAND_BG}`}
              >
                {isWorking ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <HatGlasses className="mr-2 h-4 w-4" />
                )}
                {isWorking ? 'Removing content...' : 'Create redacted copy'}
              </button>
            )}
          </div>
        </footer>
      )}
    </>
  );
}
